<template>
  <div class="esports-wrapper">
    <!-- 헤더 높이를 감산하여 iFrame 높이를 100vh로 맞출 때 사용 -->
    <div ref="header" class="app-header" v-if="showHeader">
      <slot name="header">Esports</slot>
    </div>

    <!-- iFrame이 주입될 컨테이너 -->
    <div id="esports-iframe" ref="iframeMount" class="iframe-host"></div>
  </div>
</template>

<script>
export default {
  name: 'EsportsIframe',
  props: {
    brandToken: { type: String, required: true },
    baseUrl: { type: String, required: true },
    token: { type: String, default: null },
    language: { type: String, default: 'ko' },
    currency: { type: String, default: 'KRW' },
    theme: { type: String, default: null },
    darkMode: { type: Boolean, default: true },
    oddsFormat: { type: String, default: 'DECIMAL' },
    route: { type: String, default: null },
    banners: { type: Object, default: null },
    customCurrencySymbols: { type: Array, default: () => [] },
    showHeader: { type: Boolean, default: false },
  },
  data() {
    return {
      instance: null,
      currentRouteInIframe: null,
      isScriptLoading: false,
    };
  },
  mounted() {
    console.log('🚀 Fulla iFrame 초기화 시작');
    this.initializeIframe();
    window.addEventListener('resize', this.handleResize, { passive: true });
  },
  beforeDestroy() {
    // 컴포넌트가 마운트된 상태인지 확인
    if (this.$el && this.$el.parentNode) {
      this.cleanup();
    }
  },
  destroyed() {
    // 컴포넌트가 완전히 제거된 후 추가 정리
    this.cleanup();
  },
  watch: {
    token: {
      handler(newVal) {
        this.updateToken(newVal);
      },
      immediate: true
    },
    language(val) {
      this.updateConfig({ language: val });
    },
    currency(val) {
      this.updateConfig({ currency: val });
    },
    oddsFormat(val) {
      this.updateConfig({ oddsFormat: val });
    },
    theme(val) {
      this.updateConfig({ theme: val });
    },
    darkMode(val) {
      this.updateConfig({ darkMode: val });
    },
    route(val) {
      this.updateConfig({ route: val });
    },
  },
  methods: {
    async initializeIframe() {
      try {
        await this.ensureFulla();
        this.createIframe();
      } catch (error) {
        console.error('❌ iFrame 초기화 실패:', error);
        this.$emit('error', error);
      }
    },

    async ensureFulla() {
      // 이미 로드된 경우
      if (window.fulla && typeof window.fulla.buildIframe === 'function') {
        console.log('✅ Fulla 스크립트 이미 로드됨');
        return;
      }

      // 로딩 중인 경우 대기
      if (this.isScriptLoading) {
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (window.fulla && typeof window.fulla.buildIframe === 'function') {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
      }

      // 새로 로드
      this.isScriptLoading = true;
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${this.baseUrl}/script.js`;
        script.type = 'text/javascript';
        script.async = true;
        script.setAttribute('data-fulla', '1');
        
        script.addEventListener('load', () => {
          console.log('✅ Fulla 스크립트 로드 성공');
          this.isScriptLoading = false;
          resolve();
        });
        
        script.addEventListener('error', (error) => {
          console.error('❌ Fulla 스크립트 로드 실패:', error);
          this.isScriptLoading = false;
          reject(new Error('Fulla 스크립트 로드 실패'));
        });
        
        document.head.appendChild(script);
      });
    },

    createIframe() {
      const config = this.buildConfig();
      console.log('📋 iFrame 설정:', this.sanitizeConfigForLog(config));

      try {
        // 전역 설정 방식 (정적 로더 호환)
        window.FullaConfig = config;

        // 인스턴스 방식 (동적 로더)
        if (window.fulla?.buildIframe) {
          this.instance = window.fulla.buildIframe(config, true);
          console.log('✅ iFrame 인스턴스 생성 성공');
        } else {
          console.warn('⚠️ buildIframe 함수 없음, 정적 로더 사용');
          this.instance = window.fulla?.instance || null;
        }
      } catch (error) {
        console.error('❌ iFrame 생성 실패:', error);
        throw error;
      }
    },

    buildConfig() {
      const url = new URL(window.location.href);
      const pageFromUrl = url.searchParams.get('page');
      const initialRoute = this.route || pageFromUrl || undefined;

      // 토큰 유효성 검사
      const validToken = this.token && this.token.trim() !== '' ? this.token : undefined;

      return {
        brandToken: this.brandToken,
        baseUrl: this.baseUrl,
        contentElement: '#esports-iframe',
        token: validToken, // 유효한 토큰만 전달, 없으면 undefined (로그아웃 모드)
        language: this.language,
        currency: this.currency,
        theme: this.theme || undefined,
        darkMode: this.darkMode,
        oddsFormat: this.oddsFormat,
        route: initialRoute,
        height: this.calculateHeight(),
        banners: this.banners || undefined,
        customCurrencySymbols: this.customCurrencySymbols.length ? this.customCurrencySymbols : undefined,
        eventHandler: this.handleIframeEvent,
        referer: window.location.origin, // 현재 도메인을 referer로 전달
      };
    },

    calculateHeight() {
      if (this.showHeader && this.$refs.header) {
        const headerHeight = this.$refs.header.getBoundingClientRect().height || 0;
        return Math.max(0, window.innerHeight - headerHeight);
      }
      return window.innerHeight;
    },

    updateConfig(patch) {
      if (this.instance?.updateConfig) {
        this.instance.updateConfig(patch);
      } else {
        // 정적 구성 업데이트
        window.FullaConfig = { ...window.FullaConfig, ...patch };
      }
    },

    updateToken(newToken) {
      if (this.instance?.refreshToken && newToken) {
        this.instance.refreshToken(newToken);
      } else if (this.instance?.updateConfig) {
        this.instance.updateConfig({ token: newToken });
      }
    },

    handleResize() {
      if (this.instance?.updateConfig) {
        this.instance.updateConfig({ height: this.calculateHeight() });
      }
    },

    handleIframeEvent(event) {
      console.log('📨 iFrame 이벤트:', event.type, event);
      
      switch (event.type) {
        case 'LOADED':
          console.log('✅ iFrame 로딩 완료');
          this.$emit('loaded');
          break;
          
        case 'ERROR':
          this.handleIframeError(event);
          break;
          
        case 'REQUEST_SIGN_IN':
          console.log('🔐 로그인 요청');
          this.$store.dispatch('SHOW_MODAL', 'login')
          break;
          
        case 'REQUEST_REFRESH_BALANCE':
          console.log('💰 잔액 갱신 요청');
          this.$store.dispatch('ME')
          break;
          
        case 'ROUTE_CHANGE':
          this.handleRouteChange(event.route);
          break;
          
        case 'ANALYTICS':
          console.log('📊 분석 이벤트:', event.action);
          this.$emit('analytics', event);
          break;
          
        default:
          console.log('ℹ️ 기타 이벤트:', event.type);
          break;
      }
    },

    handleIframeError(event) {
      console.error('❌ iFrame 에러:', event);
      
      // 보안 관련 에러 체크
      if (event.error?.message) {
        this.checkSecurityIssues(event.error.message);
      }
      
      this.$emit('error', event);
    },

    checkSecurityIssues(errorMessage) {
      if (errorMessage.includes('frame-ancestors')) {
        console.error('❌ CSP frame-ancestors 문제: 사이트의 CSP 설정에서 Fulla.bet 도메인 허용 필요');
      }
      if (errorMessage.includes('X-Frame-Options')) {
        console.error('❌ X-Frame-Options 문제: 서버에서 frame 임베딩 차단 중');
      }
      if (errorMessage.includes('blocked')) {
        console.error('❌ 도메인 화이트리스트 문제: Fulla 측에 현재 도메인 등록 필요');
        console.error('현재 도메인:', window.location.origin);
      }
    },

    handleRouteChange(route) {
      console.log('🛤️ 라우트 변경:', route);
      this.currentRouteInIframe = route;
      // syncRouteToUrl(route); // 제거: URL 동기화 불필요
      this.$emit('route-change', route);
    },

    sanitizeConfigForLog(config) {
      return {
        ...config,
        brandToken: config.brandToken ? `${config.brandToken.substring(0, 8)}...` : 'MISSING',
        token: config.token ? `${config.token.substring(0, 8)}...` : 'NO_TOKEN',
      };
    },

    reload() {
      console.log('🔄 iFrame 리로드 시작');
      
      // 기존 인스턴스 정리
      if (this.instance?.destroy) {
        try {
          this.instance.destroy();
        } catch (error) {
          console.warn('⚠️ 기존 iFrame destroy 중 에러:', error);
        }
      }
      
      this.instance = null;
      
      // 새로 초기화
      this.initializeIframe();
    },

    cleanup() {
      window.removeEventListener('resize', this.handleResize);
      
      if (this.instance?.destroy) {
        try {
          this.instance.destroy();
        } catch (error) {
          console.warn('⚠️ iFrame destroy 중 에러 발생 (정상적인 경우):', error);
        } finally {
          this.instance = null;
        }
      }
    },
  },
};
</script>

<style scoped>
.esports-wrapper {
  width: 100%;
  min-height: 100vh;
}

.app-header {
  flex-shrink: 0;
}

.iframe-host {
  width: 100%;
  min-height: 100vh;
}

/* iframe 스타일 */
.iframe-host :deep(iframe) {
  display: block;
  width: 100%;
  min-height: 100vh;
  border: none;
}
</style>
